/**
 * Aura Collection Carousel / Grid
 * Native scroll-snap version for Shopify Horizon
 * No transform drag. No 3D tilt.
 */
(function () {
  'use strict';

  if (window.AuraCollectionCarouselV2Loaded) return;
  window.AuraCollectionCarouselV2Loaded = true;

  class AuraCollectionCarousel {
    constructor(section) {
      this.section = section;
      this.wrapper = section.querySelector('[data-acc-wrapper]');
      this.viewport = section.querySelector('[data-acc-viewport]');
      this.track = section.querySelector('[data-acc-track]');
      this.prevButtons = Array.from(section.querySelectorAll('[data-acc-prev]'));
      this.nextButtons = Array.from(section.querySelectorAll('[data-acc-next]'));
      this.dotsContainer = section.querySelector('[data-acc-dots]');
      this.toggleButtons = Array.from(section.querySelectorAll('[data-acc-view]'));
      this.autoplayTimer = null;
      this.scrollRaf = null;
      this.currentIndex = 0;

      if (!this.wrapper || !this.viewport || !this.track) return;

      this.settings = {
        display: this.wrapper.dataset.display || 'carousel',
        autoplay: this.wrapper.dataset.autoplay === 'true',
        autoplaySpeed: parseInt(this.wrapper.dataset.autoplaySpeed, 10) || 3500,
        loop: this.wrapper.dataset.loop === 'true',
        colsDesktop: parseInt(this.wrapper.dataset.colsDesktop, 10) || 4,
        colsTablet: parseInt(this.wrapper.dataset.colsTablet, 10) || 2,
        colsMobile: parseInt(this.wrapper.dataset.colsMobile, 10) || 1
      };

      this.init();
    }

    get slides() {
      return Array.from(this.track.querySelectorAll('.acc-slide'));
    }

    get visibleCols() {
      const width = window.innerWidth;
      if (width >= 1024) return this.settings.colsDesktop;
      if (width >= 768) return this.settings.colsTablet;
      return this.settings.colsMobile;
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCols);
    }

    get gap() {
      return parseFloat(getComputedStyle(this.track).gap) || 0;
    }

    get slideStep() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 0;
      return firstSlide.getBoundingClientRect().width + this.gap;
    }

    init() {
      this.bindToggles();
      this.bindButtons();
      this.bindScroll();
      this.bindKeyboard();
      this.bindAutoplayPause();
      this.bindResize();
      this.buildDots();
      this.updateState();

      if (this.settings.display === 'carousel' && this.settings.autoplay) {
        this.startAutoplay();
      }
    }

    isCarousel() {
      return this.settings.display === 'carousel';
    }

    scrollToIndex(index, behavior = 'smooth') {
      if (!this.isCarousel()) return;

      const nextIndex = Math.max(0, Math.min(index, this.maxIndex));
      const left = nextIndex * this.slideStep;

      this.viewport.scrollTo({ left, behavior });
      this.currentIndex = nextIndex;
      this.updateState();
    }

    next() {
      if (!this.isCarousel()) return;

      if (this.currentIndex >= this.maxIndex) {
        if (this.settings.loop) this.scrollToIndex(0);
        return;
      }

      this.scrollToIndex(this.currentIndex + 1);
    }

    prev() {
      if (!this.isCarousel()) return;

      if (this.currentIndex <= 0) {
        if (this.settings.loop) this.scrollToIndex(this.maxIndex);
        return;
      }

      this.scrollToIndex(this.currentIndex - 1);
    }

    bindButtons() {
      this.prevButtons.forEach((button) => {
        button.addEventListener('click', () => this.prev());
      });

      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => this.next());
      });
    }

    bindToggles() {
      this.toggleButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.accView;
          if (!view || view === this.settings.display) return;

          this.settings.display = view;
          this.wrapper.dataset.display = view;
          this.wrapper.classList.remove('acc-display--carousel', 'acc-display--grid');
          this.wrapper.classList.add(`acc-display--${view}`);

          this.toggleButtons.forEach((item) => {
            item.classList.toggle('is-active', item === button);
          });

          if (view === 'grid') {
            this.stopAutoplay();
            this.viewport.scrollTo({ left: 0, behavior: 'auto' });
          } else {
            this.currentIndex = 0;
            this.viewport.scrollTo({ left: 0, behavior: 'auto' });
            this.buildDots();
            if (this.settings.autoplay) this.startAutoplay();
          }

          this.updateState();
        });
      });
    }

    bindScroll() {
      this.viewport.addEventListener('scroll', () => {
        if (!this.isCarousel()) return;

        if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

        this.scrollRaf = requestAnimationFrame(() => {
          const step = this.slideStep;
          if (!step) return;

          this.currentIndex = Math.max(0, Math.min(Math.round(this.viewport.scrollLeft / step), this.maxIndex));
          this.updateState();
        });
      }, { passive: true });
    }

    bindKeyboard() {
      this.viewport.setAttribute('tabindex', '0');

      this.viewport.addEventListener('keydown', (event) => {
        if (!this.isCarousel()) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.prev();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }
      });
    }

    bindAutoplayPause() {
      this.wrapper.addEventListener('mouseenter', () => this.stopAutoplay());
      this.wrapper.addEventListener('mouseleave', () => {
        if (this.settings.autoplay && this.isCarousel()) this.startAutoplay();
      });
      this.wrapper.addEventListener('focusin', () => this.stopAutoplay());
      this.wrapper.addEventListener('focusout', () => {
        if (this.settings.autoplay && this.isCarousel()) this.startAutoplay();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else if (this.settings.autoplay && this.isCarousel()) {
          this.startAutoplay();
        }
      });
    }

    bindResize() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
          this.buildDots();
          this.scrollToIndex(this.currentIndex, 'auto');
          this.updateState();
        });

        this.resizeObserver.observe(this.section);
      } else {
        window.addEventListener('resize', () => {
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
          this.buildDots();
          this.scrollToIndex(this.currentIndex, 'auto');
          this.updateState();
        });
      }
    }

    buildDots() {
      if (!this.dotsContainer) return;

      this.dotsContainer.innerHTML = '';

      if (!this.isCarousel()) return;

      const total = this.maxIndex + 1;
      if (total <= 1) return;

      for (let i = 0; i < total; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `acc-dot${i === this.currentIndex ? ' is-active' : ''}`;
        dot.setAttribute('aria-label', `Go to collection slide ${i + 1}`);
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-selected', String(i === this.currentIndex));
        dot.addEventListener('click', () => this.scrollToIndex(i));
        this.dotsContainer.appendChild(dot);
      }
    }

    updateState() {
      const carousel = this.isCarousel();
      const atStart = this.currentIndex <= 0;
      const atEnd = this.currentIndex >= this.maxIndex;

      this.prevButtons.forEach((button) => {
        button.disabled = !carousel || (!this.settings.loop && atStart);
        button.setAttribute('aria-disabled', String(button.disabled));
      });

      this.nextButtons.forEach((button) => {
        button.disabled = !carousel || (!this.settings.loop && atEnd);
        button.setAttribute('aria-disabled', String(button.disabled));
      });

      if (this.dotsContainer) {
        this.dotsContainer.querySelectorAll('.acc-dot').forEach((dot, index) => {
          const active = index === this.currentIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
        });
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.isCarousel()) return;

      this.autoplayTimer = window.setInterval(() => this.next(), this.settings.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  }

  function initAuraCollectionCarousels(root = document) {
    root.querySelectorAll('.acc-section').forEach((section) => {
      if (!section._auraCollectionCarousel) {
        section._auraCollectionCarousel = new AuraCollectionCarousel(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAuraCollectionCarousels());
  } else {
    initAuraCollectionCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAuraCollectionCarousels(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.acc-section');
    if (section && section._auraCollectionCarousel) {
      section._auraCollectionCarousel.destroy();
      section._auraCollectionCarousel = null;
    }
  });
})();
